/**
 * Patient Packages — read queries (PRD.261).
 *
 * Read-side QuerySpecs for patient-owned packages and their snapshotted item
 * balances. Writes (create-from-invoice, payment sync, void) are performed
 * imperatively by the web-admin patient-package service so PowerSync records
 * CRUD entries per row.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/** A raw `patient_packages` row (SQLite representation). */
export interface PatientPackageRow {
  id: string;
  org_id: string | null;
  patient_id: string | null;
  clinic_id: string | null;
  source_invoice_id: string | null;
  source_invoice_item_id: string | null;
  package_inventory_item_id: string | null;
  package_name_snapshot: string | null;
  package_code_snapshot: string | null;
  package_price_snapshot: number | null;
  purchase_date: string | null;
  valid_until: string | null;
  validity_months_snapshot: number | null;
  payment_status: string | null;
  redemption_status: string | null;
  activated_at: string | null;
  activated_by: string | null;
  activation_reason: string | null;
  suspended_at: string | null;
  suspended_by: string | null;
  suspension_reason: string | null;
  voided_at: string | null;
  voided_by: string | null;
  void_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

/**
 * A patient package joined with its source invoice's settlement so the drawer
 * can show Paid / Total / Balance without a second query. Amounts are in minor
 * units (cents).
 */
export interface PatientPackageWithBalanceRow extends PatientPackageRow {
  grand_cents: number | null;
  paid_cents: number | null;
  balance_cents: number | null;
  item_count: number | null;
}

/** A raw `patient_package_items` snapshot row. */
export interface PatientPackageItemRow {
  id: string;
  org_id: string | null;
  patient_package_id: string | null;
  child_inventory_item_id: string | null;
  item_name_snapshot: string | null;
  item_code_snapshot: string | null;
  item_type_snapshot: string | null;
  total_quantity: number | null;
  redeemed_quantity: number | null;
  remaining_quantity: number | null;
  unit_price_snapshot: number | null;
  sort_order: number | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/**
 * List a patient's owned packages (newest first), joined with the source
 * invoice's paid/grand totals and an active included-item count. Excludes
 * soft-deleted (voided) rows.
 */
export function qPatientPackagesForPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_packages', 'by-patient', patientId] as const,
    sql: `
      SELECT
        pp.*,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) AS grand_cents,
        COALESCE(paid.paid_cents, 0) AS paid_cents,
        CAST(ROUND(COALESCE(i.grand_total, 0) * 100) AS INTEGER) - COALESCE(paid.paid_cents, 0)
          AS balance_cents,
        COALESCE(items.cnt, 0) AS item_count
      FROM patient_packages pp
      LEFT JOIN invoices i ON i.id = pp.source_invoice_id
      LEFT JOIN (
        SELECT invoice_id, COALESCE(SUM(amount_cents), 0) AS paid_cents
        FROM invoice_payments
        WHERE invoice_id IS NOT NULL
        GROUP BY invoice_id
      ) paid ON paid.invoice_id = pp.source_invoice_id
      LEFT JOIN (
        SELECT patient_package_id, COUNT(*) AS cnt
        FROM patient_package_items
        WHERE deleted_at IS NULL
        GROUP BY patient_package_id
      ) items ON items.patient_package_id = pp.id
      WHERE pp.patient_id = ?
        AND pp.deleted_at IS NULL
      ORDER BY pp.created_at DESC
    `,
    params: [patientId],
    watch: ['patient_packages', 'patient_package_items', 'invoices', 'invoice_payments'],
  };
}

/** List a patient package's active included item balances, ordered for display. */
export function qPatientPackageItems(patientPackageId: string): QuerySpec<[string]> {
  return {
    key: ['patient_package_items', 'by-package', patientPackageId] as const,
    sql: `
      SELECT *
      FROM patient_package_items
      WHERE patient_package_id = ?
        AND deleted_at IS NULL
      ORDER BY sort_order ASC, item_name_snapshot ASC
    `,
    params: [patientPackageId],
    watch: ['patient_package_items'],
  };
}

/**
 * Count a patient's active (non-voided) packages — used for the sidebar badge.
 */
export function qPatientPackageCount(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_packages', 'count', patientId] as const,
    sql: `SELECT COUNT(*) AS cnt FROM patient_packages WHERE patient_id = ? AND deleted_at IS NULL`,
    params: [patientId],
    watch: ['patient_packages'],
  };
}
